import type { FC } from 'react';
import { useCallback, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { PublicLayout, HttpStatus } from '@catalyze/ui';
import { CheckEmailStatus } from 'api/types/user';
import { useCheckEmailMutation } from 'api/users/checkEmail';
import { useSignUpMutation } from 'api/users/signUpUser';
import { CardContainer } from 'features/SignUp/components/CardContainer';
import { AccountDetailsForm } from 'features/SignUp/components/forms/AccountDetailsForm';
import { BrokerDetailForm } from 'features/SignUp/components/forms/BrokerDetailForm';
import { EmailForm } from 'features/SignUp/components/forms/EmailForm';
import { LocationForm } from 'features/SignUp/components/forms/LocationForm';
import { Navigation } from 'features/SignUp/components/Navigation';
import { useCDP } from 'features/SignUp/hooks/useCDP';
import { useCreatePasswordRedirect } from 'features/SignUp/hooks/useCreatePasswordRoute';
import { SignUpSteps } from 'features/SignUp/types/signUpForm';
import type {
    IContactForm,
    IAccountDetailsForm,
    ILocationForm,
    IBrokerDetailForm,
    ContactErrors,
} from 'features/SignUp/types/signUpForm';

export const SignUp: FC = () => {
    const [currentStep, setCurrentStep] = useState(SignUpSteps.STEP_ONE);
    const [contactData, setContactData] = useState<IContactForm | undefined>();
    const [contactErrors, setContactErrors] = useState<ContactErrors>({});
    const [accountData, setAccountData] = useState<
        IAccountDetailsForm | undefined
    >();
    const [locationData, setLocationData] = useState<
        ILocationForm | undefined
    >();
    const [brokerDetailData, setBrokerDetailData] = useState<
        IBrokerDetailForm | undefined
    >();
    const {
        mutate: signUp,
        data: signUpResult,
        isLoading: signUpLoading,
    } = useSignUpMutation({});
    const redirect = useCreatePasswordRedirect();
    const [searchParams] = useSearchParams();

    const platform: string = searchParams.get('product_platform') ?? '';

    if (platform) {
        localStorage.setItem('product_platform', platform);
    }

    const setEmailError = useCallback(
        (message?: string) => {
            const currentState: ContactErrors = { ...contactErrors };
            if (!message) {
                delete currentState.email;
            } else {
                currentState.email = message;
            }
            setContactErrors(currentState);
        },
        [contactErrors, setContactErrors]
    );

    const setPhoneError = useCallback(
        (message?: string) => {
            const currentState: ContactErrors = { ...contactErrors };
            if (!message) {
                delete currentState.phone;
            } else {
                currentState.phone = message;
            }
            setContactErrors(currentState);
        },
        [contactErrors, setContactErrors]
    );

    const {
        mutate: checkEmail,
        data: checkEmailResult,
        isLoading: checkEmailLoading,
        error: checkEmailError,
    } = useCheckEmailMutation({});

    useEffect(() => {
        const isNewError =
            checkEmailError?.response.status === HttpStatus.CONFLICT;
        const isExistingError = contactErrors.email !== undefined;
        if (isNewError !== isExistingError) {
            setEmailError(isNewError ? 'Email is already used' : undefined);
        }
    }, [checkEmailError, contactErrors, setEmailError]);

    useEffect(() => {
        if (!signUpLoading && signUpResult?.redirectUrl) {
            window.location.href = signUpResult.redirectUrl;
        }
    }, [signUpResult, signUpLoading]);

    const isCDPavailable = useCDP();

    const handleSubmitFirstStep = useCallback(
        (data: IContactForm) => {
            checkEmail({ email: data.email });
            setPhoneError(
                !checkPhone(data.phone) ? 'Phone can not be blank' : undefined
            );
            setContactData(data);
        },
        [checkEmail, setPhoneError]
    );

    const pingCDP = () => {
        if (!isCDPavailable) return;

        // @ts-ignore uipe is magic function from CDP lib
        window.uipe('track', 'Lead', {
            keys: {
                email: contactData?.email,
                phone: contactData?.phone,
            },
            lead: {
                firstName: accountData?.firstName,
                lastName: accountData?.lastName,
            },
        });
    };

    useEffect(() => {
        if (!checkEmailLoading && checkEmailResult?.status) {
            pingCDP();
            switch (checkEmailResult.status) {
                case CheckEmailStatus.CONTRACT_ALREADY_SIGNED:
                    redirect('');
                    break;
                case CheckEmailStatus.NEW_SIGNUP:
                    if (!Object.keys(contactErrors).length) {
                        setCurrentStep(SignUpSteps.STEP_TWO);
                    }
                    break;
                default:
                    break;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkEmailResult, checkEmailLoading, contactErrors]);

    const handleSubmitForm = useCallback(
        (location: ILocationForm) => {
            setLocationData(location);
            signUp({
                data: {
                    ...contactData,
                    ...accountData,
                    ...location,
                    ...brokerDetailData,
                },
            });
        },
        [contactData, accountData, brokerDetailData, signUp]
    );

    return (
        <PublicLayout navigation={Navigation}>
            <CardContainer>
                {currentStep === SignUpSteps.STEP_ONE && (
                    <EmailForm
                        initialValues={contactData}
                        onSubmit={handleSubmitFirstStep}
                        errors={contactErrors}
                        loading={checkEmailLoading}
                    />
                )}
                {currentStep === SignUpSteps.STEP_TWO && (
                    <AccountDetailsForm
                        initialValues={accountData}
                        onSubmit={(data: IAccountDetailsForm) => {
                            data.age ??= 0;
                            data.gender ??= 'Other';
                            setAccountData(data);
                            pingCDP();
                            setCurrentStep(SignUpSteps.STEP_FOUR);
                        }}
                        onGoBack={(data: IAccountDetailsForm) => {
                            data.age ??= 0;
                            data.gender ??= 'Other';
                            setAccountData(data);
                            setCurrentStep(SignUpSteps.STEP_ONE);
                        }}
                    />
                )}
                {currentStep === SignUpSteps.STEP_THREE && (
                    <BrokerDetailForm
                        initialValues={brokerDetailData}
                        onSubmit={(data: IBrokerDetailForm) => {
                            setBrokerDetailData(data);
                            setCurrentStep(SignUpSteps.STEP_FOUR);
                        }}
                        onGoBack={(data: IBrokerDetailForm) => {
                            setBrokerDetailData(data);
                            setCurrentStep(SignUpSteps.STEP_TWO);
                        }}
                    />
                )}
                {currentStep === SignUpSteps.STEP_FOUR && (
                    <LocationForm
                        initialValues={locationData}
                        onSubmit={handleSubmitForm}
                        onGoBack={(data: ILocationForm) => {
                            setLocationData(data);
                            setCurrentStep(SignUpSteps.STEP_TWO);
                        }}
                        loading={signUpLoading}
                    />
                )}
            </CardContainer>
        </PublicLayout>
    );
};

function checkPhone(input: string) {
    return input.length !== 0;
    // return /^[0-9]{3}-[0-9]{3}-[0-9]{4}$/u.test(input);
}
